// src/state/session.ts: tenant, site flags and the signed-in person (spec 2.5).
import { signal, type Signal } from "@preact/signals";
import type { Me, PublicTenant, SiteConfig, TeamTenant } from "../../shared/api";
import { parsePwaMode, pwaAllows } from "../../shared/pwa";
import { navigate } from "../app/router";
import { api } from "../lib/api";
import { setTimeZone } from "../lib/format";
import { ApiError } from "../lib/http";
import { inApp } from "../lib/pwa";
import { applyTheme } from "../lib/theme";

export const SIGNED_OUT: Me = { user: null, staff: null, projects: [], scopeProjectId: null };

export const tenant: Signal<PublicTenant | null> = signal(null);            // from GET /api/tenant
export const site: Signal<SiteConfig | null> = signal(null);
export const me: Signal<Me | null> = signal(null);                          // from GET /api/me; me.value.user === null when signed out
export const teamTenant: Signal<TeamTenant | null> = signal(null);          // loaded lazily for staff
/** "unknown-host": GET /api/tenant answered 404; "error": boot failed for another reason. */
export const bootState: Signal<"loading" | "ready" | "unknown-host" | "error"> = signal("loading");

async function loadMe(): Promise<Me> {
  try {
    return await api.me();
  } catch (e) {
    // 401/404 mean "no session" (or, before T2's /api/me lands, "no route"): treat as signed out.
    if (e instanceof ApiError && (e.status === 401 || e.status === 404)) return SIGNED_OUT;
    throw e;
  }
}

/** Tenant and me in parallel, then the theme (brand tokens, fonts, theme color, app meta, title) and the time zone. */
export async function boot(): Promise<void> {
  bootState.value = "loading";
  try {
    const [t, m] = await Promise.all([api.getTenant(), loadMe()]);
    tenant.value = t.tenant;
    site.value = t.site;
    me.value = m;
    applyTheme(t.tenant.brand, { title: t.tenant.name, standalone: inApp(), installable: pwaAllows(parsePwaMode(t.site.pwa), "install") });
    setTimeZone(t.tenant.region.timezone);
    bootState.value = "ready";
  } catch (e) {
    bootState.value = e instanceof ApiError && e.status === 404 ? "unknown-host" : "error";
  }
}

export async function refreshMe(): Promise<void> {
  me.value = await loadMe();
}

/** The builder's public config again, and the team's view of it on the next read: homes added while it is in setup show in the sidebar and Our Homes. */
export async function refreshTenant(): Promise<void> {
  const t = await api.getTenant();
  tenant.value = t.tenant;
  teamTenant.value = null;
}

export async function loadTeamTenant(): Promise<TeamTenant> {
  if (teamTenant.value) return teamTenant.value;
  const t = await api.getTeamTenant();
  teamTenant.value = t;
  return t;
}

/**
 * The address a browser just lost its board session for, when that was the limited session Start Your Build set
 * (spec 5.3, ONSITE 13: the email was confirmed on another device). /signin starts from it, so one tap sends a
 * link and a code. In memory only: a reload forgets it.
 */
export const signInHint: Signal<{ email: string } | null> = signal(null);

/**
 * Clears me and navigates to /signin?next= (used on any 401). On the public test ride the page loads "/" again instead:
 * the visitor's copy may have ended (the landing), or they are between personas (the tour).
 */
export function signedOut(next?: string): void {
  if (site.value?.trial) {
    window.location.assign("/");
    return;
  }
  const u = me.value?.user;
  signInHint.value = u && u.sessionOrigin === "intake" && u.email ? { email: u.email } : null;
  me.value = SIGNED_OUT;
  teamTenant.value = null;
  // A second 401 that lands after the first has already sent the person to /signin would name /signin as the place to
  // go back to and lose the real one (/team): never name the sign-in pages, and leave the page already showing alone.
  const nameable = !!next && next.startsWith("/") && !next.startsWith("//") && !/^\/(signin|auth\/)/.test(next);
  const onSignIn = typeof location !== "undefined" && /^\/signin(\/|$)/.test(location.pathname);
  if (onSignIn && !nameable) return;
  const q = nameable ? `?next=${encodeURIComponent(next!)}` : "";
  navigate(`/signin${q}`, { replace: true });
}
